import { useState, useEffect } from "react";
import { authHeaders } from "../lib/auth";
import AppHeader from "./AppHeader";

// ── Reports & History ─────────────────────────────────────────────────────────
// Charts only what we truly retain: bottle-verified intake per day and the daily
// biometric trend (both from the backend), plus the device-local water-test log.
// No fabricated NHS or manual-intake series. Sparse data is shown honestly.
const C = {
  white:"#FFFFFF", bg:"#F8FAFB", border:"#E8EEF2",
  blue:"#51B0E6", blueDark:"#2A8FCA", blueLight:"#EDF6FC",
  body:"#1A2535", muted:"#6B7A8D", green:"#1E8A4C",
  amber:"#C89B3C", red:"#B84A4A",
};

const API_BASE = 'https://generosity-sales-engine-mvp-api.onrender.com';
// Phase 2: identity rides via authHeaders() (X-WTR-Auth session token).
const TEST_HIST_KEY = "wtr_test_history"; // written by WTRBottleScreen, device-local

function RIcon({ name, size=20, color=C.blue }) {
  const s=size, ic=color;
  const icons = {
    drop: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M12 3s6 7 6 11a6 6 0 0 1-12 0c0-4 6-11 6-11Z" stroke={ic} strokeWidth="1.6" strokeLinejoin="round"/></svg>,
    pulse: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M3 12h4l2.5-7 4 14 2.5-7H21" stroke={ic} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    flask: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M9 3v7l-5 8c-.5 1 0 2 1 2h14c1 0 1.5-1 1-2l-5-8V3" stroke={ic} strokeWidth="1.6" strokeLinejoin="round"/><line x1="8" y1="3" x2="16" y2="3" stroke={ic} strokeWidth="1.6" strokeLinecap="round"/></svg>,
  };
  return <span style={{display:"inline-flex",alignItems:"center",justifyContent:"center"}}>{icons[name]||icons.drop}</span>;
}

function Section({ icon, title, sub, children }) {
  return (
    <div style={{background:C.white,borderRadius:18,border:`1.5px solid ${C.border}`,padding:"16px"}}>
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:14}}>
        <div style={{width:38,height:38,borderRadius:11,flexShrink:0,background:C.blueLight,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <RIcon name={icon} size={20} color={C.blueDark}/>
        </div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:15,fontWeight:800,color:C.body}}>{title}</div>
          {sub && <div style={{fontSize:11,color:C.muted,marginTop:1}}>{sub}</div>}
        </div>
      </div>
      {children}
    </div>
  );
}

function Empty({ children }) {
  return <div style={{fontSize:13,color:C.muted,lineHeight:1.6,padding:"4px 0 2px"}}>{children}</div>;
}

// Responsive bar chart from divs (no chart lib). data: [{date, value}]
function Bars({ data, color }) {
  const max = Math.max(...data.map(d => d.value), 0.001);
  return (
    <div style={{display:"flex",alignItems:"flex-end",gap:3,height:120,marginTop:4}}>
      {data.map((d, i) => (
        <div key={i} title={`${d.date}: ${d.value}`} style={{flex:1,display:"flex",flexDirection:"column",justifyContent:"flex-end",height:"100%"}}>
          <div style={{height:`${Math.max(2,(d.value/max)*100)}%`,background:color,borderRadius:"3px 3px 0 0",minHeight:2,opacity:0.85}}/>
        </div>
      ))}
    </div>
  );
}

function Spark({ data, color, w=110, h=34 }) {
  const pts = data.filter(v => v != null);
  if (pts.length < 2) return <div style={{width:w,height:h}}/>;
  const min = Math.min(...pts), max = Math.max(...pts), rng = max - min || 1;
  const path = pts.map((v, i) => `${(i/(pts.length-1))*w},${h-((v-min)/rng)*(h-6)-3}`).join(" ");
  return (
    <svg width={w} height={h} style={{display:"block",overflow:"visible"}}>
      <polyline points={path} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function BioTrend({ label, series, color, unit }) {
  const pts = series.filter(v => v != null);
  const latest = pts.length ? pts[pts.length - 1] : null;
  return (
    <div style={{flex:1,minWidth:0,background:C.bg,borderRadius:12,padding:"12px 12px 10px"}}>
      <div style={{fontSize:10,fontWeight:700,color:C.muted,letterSpacing:0.5,textTransform:"uppercase"}}>{label}</div>
      <div style={{display:"flex",alignItems:"baseline",gap:3,margin:"4px 0 6px"}}>
        <span style={{fontSize:22,fontWeight:900,color:latest!=null?C.body:C.muted,fontFamily:"Nunito,sans-serif",lineHeight:1}}>{latest!=null?latest:"\u2014"}</span>
        {unit && latest!=null && <span style={{fontSize:10,color:C.muted,fontWeight:700}}>{unit}</span>}
      </div>
      <Spark data={series} color={color}/>
    </div>
  );
}

function tdsColor(ppm) {
  if (ppm == null) return C.blue;
  if (ppm <= 35) return C.green;
  if (ppm <= 150) return C.blue;
  if (ppm <= 300) return C.amber;
  return C.red;
}
function relTime(ts) {
  const m = Math.floor((Date.now() - ts) / 60000);
  if (m < 1) return "Just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(ts).toLocaleDateString();
}

export default function ReportsScreen({ onClose }) {
  const [email] = useState(() => {
    try { const p = JSON.parse(localStorage.getItem('wtr_profile') || '{}'); return (p.email || '').trim().toLowerCase(); }
    catch(e) { return ''; }
  });
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [tests] = useState(() => {
    try { return JSON.parse(localStorage.getItem(TEST_HIST_KEY) || '[]'); } catch(e) { return []; }
  });

  const validEmail = email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  useEffect(() => {
    if (!validEmail) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/wtr/hydration/history?units=l&days=30&email=${encodeURIComponent(email)}`, { headers: authHeaders() });
        if (r.ok) {
          const j = await r.json();
          if (!cancelled && j.ok) setData(j);
        }
      } catch(e) { /* leave null */ }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [email, validEmail]);

  const intake = data?.intakeByDay || [];
  const bio = data?.biometricsByDay || [];
  const units = data?.units === 'oz' ? 'oz' : 'L';
  const intakeTotal = intake.reduce((a, d) => a + (d.verified || 0), 0);
  const intakeDays = intake.filter(d => d.verified > 0).length;
  const intakeAvg = intakeDays ? (intakeTotal / intakeDays) : 0;
  const recentTests = [...tests].reverse().slice(0, 10);

  if (loading) {
    return (
      <div style={{fontFamily:"Nunito Sans,-apple-system,sans-serif",background:C.white,minHeight:"100%",display:"flex",alignItems:"center",justifyContent:"center"}}>
        <div style={{textAlign:"center"}}>
          <div style={{width:40,height:40,border:`3px solid ${C.border}`,borderTopColor:C.blue,borderRadius:"50%",animation:"spin 0.8s linear infinite",margin:"0 auto 12px"}}/>
          <div style={{fontSize:14,color:C.muted,fontWeight:600}}>Loading your history...</div>
          <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="reports-screen" style={{fontFamily:"Nunito Sans,-apple-system,sans-serif",background:C.white,minHeight:"100%",color:C.body,display:"flex",flexDirection:"column",maxWidth:480,margin:"0 auto",WebkitFontSmoothing:"antialiased"}}>
      <link href="https://fonts.googleapis.com/css2?family=Nunito:wght@700;800;900&family=Nunito+Sans:wght@400;600;700&display=swap" rel="stylesheet"/>
      <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}} @keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}} *{box-sizing:border-box;-webkit-tap-highlight-color:transparent}`}</style>

      <AppHeader
        variant="modal"
        title="Reports & History"
        onClose={onClose}
      />

      <div style={{flex:1,overflowY:"auto",padding:"20px",display:"flex",flexDirection:"column",gap:16,paddingBottom:40,animation:"fadeIn 0.3s ease"}}>

        {!validEmail && (
          <div style={{padding:"16px 18px",background:`${C.amber}10`,border:`1.5px solid ${C.amber}30`,borderRadius:16,fontSize:13,fontWeight:600,color:C.body}}>
            Set up your Profile to see your history.
          </div>
        )}

        {/* HYDRATION (verified intake per day) */}
        <Section icon="drop" title="Hydration" sub="Verified by your WTR Bottle">
          {intake.length > 0 ? (
            <div>
              <div style={{display:"flex",gap:20,marginBottom:6}}>
                <div>
                  <div style={{fontSize:24,fontWeight:900,color:C.body,fontFamily:"Nunito,sans-serif",lineHeight:1}}>{intakeAvg.toFixed(1)}<span style={{fontSize:12,color:C.muted,fontWeight:700,marginLeft:3}}>{units}/day</span></div>
                  <div style={{fontSize:11,color:C.muted,marginTop:3}}>Average on days with a sync</div>
                </div>
                <div>
                  <div style={{fontSize:24,fontWeight:900,color:C.body,fontFamily:"Nunito,sans-serif",lineHeight:1}}>{intakeDays}</div>
                  <div style={{fontSize:11,color:C.muted,marginTop:3}}>Days with verified intake</div>
                </div>
              </div>
              <Bars data={intake.map(d => ({ date: d.date, value: d.verified }))} color={C.blue}/>
              <div style={{fontSize:11,color:C.muted,marginTop:10,lineHeight:1.5}}>
                Shows water your bottle verified. Manual logs are not yet included in history.
              </div>
            </div>
          ) : (
            <Empty>Sync your WTR Bottle to start building your hydration history. Verified intake will chart here day by day.</Empty>
          )}
        </Section>

        {/* BIOMETRICS (trend) */}
        <Section icon="pulse" title="Biometrics" sub="From your connected wearable">
          {bio.some(d => d.readiness != null || d.sleep != null || d.hrv != null) ? (
            <div style={{display:"flex",gap:10}}>
              <BioTrend label="Readiness" series={bio.map(d => d.readiness)} color={C.blue}/>
              <BioTrend label="Sleep" series={bio.map(d => d.sleep)} color={C.blueDark}/>
              <BioTrend label="HRV" series={bio.map(d => d.hrv)} color={C.green} unit="ms"/>
            </div>
          ) : (
            <Empty>Connect a wearable in Devices & Integrations to see your readiness, sleep, and HRV trends build over time.</Empty>
          )}
          {bio.length > 0 && bio.length < 3 && (
            <div style={{fontSize:11,color:C.muted,marginTop:10,lineHeight:1.5}}>
              Trends sharpen as more days are recorded. Keep your wearable connected.
            </div>
          )}
        </Section>

        {/* WATER QUALITY TESTS (device-local) */}
        <Section icon="flask" title="Water Quality Tests" sub="Recent tests on this device">
          {recentTests.length > 0 ? (
            <div>
              {recentTests.map((t, i) => {
                const c = tdsColor(t.tds);
                return (
                  <div key={t.ts} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"11px 0",borderTop:i>0?`1px solid ${C.border}`:"none"}}>
                    <div style={{display:"flex",alignItems:"center",gap:12}}>
                      <div style={{width:38,height:38,borderRadius:11,background:`${c}14`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                        <span style={{fontSize:14,fontWeight:800,color:c}}>{t.score}</span>
                      </div>
                      <div>
                        <div style={{fontSize:13,fontWeight:700,color:C.body}}>Score {t.score}/100</div>
                        <div style={{fontSize:11,color:C.muted}}>{relTime(t.ts)}</div>
                      </div>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <span style={{fontSize:16,fontWeight:800,color:c,fontVariantNumeric:"tabular-nums"}}>{t.tds}</span>
                      <span style={{fontSize:11,color:C.muted,marginLeft:3}}>ppm</span>
                    </div>
                  </div>
                );
              })}
              <div style={{fontSize:11,color:C.muted,marginTop:10,lineHeight:1.5}}>
                Saved on this device. Cross-device test history is on the roadmap.
              </div>
            </div>
          ) : (
            <Empty>Run a water test on your bottle to start tracking water quality over time.</Empty>
          )}
        </Section>

        <div style={{fontSize:11,color:C.muted,textAlign:"center",lineHeight:1.5,padding:"4px 8px"}}>
          Generosity{"\u2122"} estimates hydration and water quality to guide your choices. It is not medical advice.
        </div>

      </div>
    </div>
  );
}
